import { useEffect } from "react";

/**
 * useSEO — declarative per-route SEO without react-helmet.
 *
 * Updates document.title + meta tags as the user navigates. Falls back to the
 * defaults baked into public/index.html when no override is provided.
 *
 * Usage:
 *   useSEO({
 *     title: "BScanIA — Mentor IA personal",
 *     description: "...",
 *     canonical: "https://bscan.app/",
 *     lang: "es",
 *     ogImage: "https://bscan.app/og-image.jpg",
 *     keywords: "..."
 *   });
 *
 * Safe to call from any page component (no-op on SSR-less SPAs without DOM).
 */
export const useSEO = ({
  title,
  description,
  canonical,
  lang,
  ogImage = "https://bscan.app/og-image.jpg",
  keywords,
  noIndex = false,
} = {}) => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = {
      title: document.title,
      htmlLang: document.documentElement.lang,
    };

    if (title) document.title = title;
    if (lang) document.documentElement.lang = lang;

    const setMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        if (attrName) el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    }

    // Canonical
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }

    // robots
    if (noIndex) {
      setMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow");
    }

    return () => {
      // Restore baseline on unmount (so navigating back to a page without useSEO
      // doesn't carry over the previous title).
      document.title = prev.title;
      document.documentElement.lang = prev.htmlLang;
    };
  }, [title, description, canonical, lang, ogImage, keywords, noIndex]);
};

export default useSEO;
